export const SUPPORTED_LOCALES = ['en', 'de', 'fr'];
export const DEFAULT_LOCALE = 'en';
const REDDIT_FREE_TIER_TESTIMONIAL_URL = 'https://www.reddit.com/r/software/comments/1mby7av/comment/nforwsi/';
const REDDIT_ACCURACY_TESTIMONIAL_URL = 'https://www.reddit.com/r/Entrepreneurs/comments/1q4a1w9/comment/om5o4dl/';
const TRUSTPILOT_REVIEWS_URL = 'https://trustpilot.com/review/instanttranscriber.com';
export const LOCALIZED_PSEO_PREFIXES = ['/transcribe', '/export', '/compare', '/features'];
export const LOCALIZED_STATIC_PATHS = [
  '/api',
  '/about',
  '/blog',
  '/contact',
  '/desktop',
  '/faq',
  '/immediate-transcription',
  '/privacy',
  '/terms',
  '/use-cases',
];

export const LOCALE_LABELS = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Francais',
};

export const LOCALE_FLAGS = {
  en: '🇺🇸',
  de: '🇩🇪',
  fr: '🇫🇷',
};

export function normalizeLocale(locale) {
  const value = String(locale || '').toLowerCase().split('-')[0];
  return SUPPORTED_LOCALES.includes(value) ? value : DEFAULT_LOCALE;
}

export function localePath(path, locale) {
  const normalizedLocale = normalizeLocale(locale);
  const cleanPath = path || '/';
  if (normalizedLocale === DEFAULT_LOCALE) return cleanPath;
  if (cleanPath === '/') return `/${normalizedLocale}`;
  if (cleanPath.startsWith('/#')) return `/${normalizedLocale}${cleanPath.slice(1)}`;
  if (isLocalizedLandingPath(cleanPath)) return `/${normalizedLocale}${cleanPath}`;
  return cleanPath;
}

export function isLocalizedPseoPath(path) {
  const cleanPath = path || '/';
  return LOCALIZED_PSEO_PREFIXES.some((prefix) => cleanPath === prefix || cleanPath.startsWith(`${prefix}/`));
}

export function isLocalizedStaticPath(path) {
  const cleanPath = path || '/';
  return LOCALIZED_STATIC_PATHS.includes(cleanPath);
}

export function isLocalizedLandingPath(path) {
  return isLocalizedStaticPath(path) || isLocalizedPseoPath(path);
}

export const siteCopy = {
  en: {
    nav: {
      tagline: 'Audio & Video -> Text',
      homeLabel: 'InstantTranscriber home',
      features: 'Features',
      tools: 'Tools',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      useCases: 'Use Cases',
      blog: 'Blog',
      faq: 'FAQ',
      login: 'Log in',
      signup: 'Sign up now',
      workspace: 'My transcripts',
      account: 'My account',
      guest: 'Guest session',
      completeSignup: 'Complete signup',
      signedIn: 'Signed in',
      savedToAccount: 'Your transcripts are saved to your account.',
      toggleMenu: 'Toggle menu',
      language: 'Language',
    },
    footer: {
      tagline: 'Convert your audio to text with our AI-powered service.',
      product: 'Product',
      resources: 'Resources',
      company: 'Company',
      legal: 'Legal',
      transcribe: 'Transcribe Audio & Video',
      exportFormats: 'Export Formats',
      api: 'Transcription API',
      features: 'Features',
      recordTab: 'Transcribe Chrome Tab Audio',
      youtube: 'Import YouTube Captions',
      pricing: 'Pricing',
      compare: 'Compare Formats',
      alternatives: 'Transcription Alternatives',
      immediate: 'Immediate Transcription',
      blog: 'Blog',
      faq: 'FAQ',
      about: 'About Us',
      contact: 'Contact',
      affiliate: 'Affiliate Program',
      terms: 'Terms of Use',
      privacy: 'Privacy Policy',
      rights: 'All Rights Reserved.',
      madeFor: 'Made for content creators everywhere',
    },
    cta: {
      google: 'Start free with Google',
      googleOneClick: 'Start free with Google (1-click)',
      email: 'Or start with your email',
      emailSignup: 'Sign up with email address',
      login: 'Log in',
      microcopy: 'Instant access. No credit card required.',
      starting: 'Starting...',
      signingIn: 'Signing you in...',
      authNotConfigured: 'Authentication service is not configured',
      googleError: 'Failed to sign up with Google',
      pseoNote: 'Create a free account to keep your history and access transcripts on other devices.',
    },
    metadata: {
      title: 'Online Transcriber - Instant AI Transcription Free | InstantTranscriber',
      description:
        'Transcribe audio and video to text without signing up. Upload a file or record audio, then export transcripts with speaker labels and timestamps. Try 3 free a day.',
    },
    home: {
      heroEyebrow: 'FREE ONLINE AUDIO & VIDEO TRANSCRIPTION',
      heroTitlePrefix: 'Audio to text.',
      heroTitleRest: 'No signup needed.',
      heroSubtitle: 'Upload, paste a link or record. Get searchable text with speakers, timestamps and exports.',
      heroCompletedTranscripts: 'completed transcripts',
      heroProofDate: 'As of ${date}',
      heroReviewSource: 'Read Reddit comment',
      heroPremiumBenefit: 'Unlimited web transcription',
      heroPremiumAnnual: '${annualTotal} billed yearly',
      heroPremiumMonthly: 'Or ${price} billed monthly',
      heroPremiumLimits: 'Up to 10 h / 3 GB per file',
      heroPremiumPlans: 'View plans',
      premiumGoogle: 'Start Premium with Google',
      premiumEmail: 'Continue with email',
      premiumStart: 'Start Premium',
      intentLinksLabel: 'Popular transcription searches',
      intentLinksTitle: 'Popular:',
      intentLinks: [
        { label: 'YouTube transcript generator', href: '/transcribe/youtube-to-text' },
        { label: 'Transcript instantly online', href: '/immediate-transcription' },
        { label: 'Transcripción instantánea', href: '/immediate-transcription' },
        { label: 'Transkrip instan', href: '/immediate-transcription' },
        { label: 'Transcribe audio online', href: '/transcribe' },
        { label: 'SoundCloud transcript', href: '/transcribe/soundcloud-to-text' },
        { label: 'Audio speed changer online free', href: '/tools/audio-speed-changer' },
      ],
      workflowSearchTitle: 'Find your transcription workflow',
      workflowSearchSubtitle: 'Start from the exact job you searched for, then upload, import, record, or export in the format you need.',
      workflowSearchLinks: [
        { label: 'YouTube transcript generator', href: '/transcribe/youtube-to-text', body: 'Paste a public YouTube link and import available captions as one searchable transcript job. Try without signup.' },
        { label: 'Transcript instantly online', href: '/immediate-transcription', body: 'Upload audio or video and get searchable text in minutes.' },
        { label: 'Transcribe audio online', href: '/transcribe', body: 'Choose a file, link, browser recording, or source-specific guide.' },
        { label: 'Transcribe a podcast to text', href: '/use-cases/podcast-transcription-for-listeners', body: 'Create searchable podcast transcripts for quotes, notes, and reuse.' },
        { label: 'SoundCloud transcript generator', href: '/transcribe/soundcloud-to-text', body: 'Paste a public SoundCloud track link and create a searchable SoundCloud transcript.' },
        { label: 'Sales call transcription software', href: '/use-cases/sales-call-transcription', body: 'Review calls, coach reps, and create cleaner sales follow-ups.' },
        { label: 'Meeting recording to notes', href: '/use-cases/how-to-transcribe-a-meeting-recording', body: 'Turn Zoom, Meet, or browser meeting recordings into notes.' },
        { label: 'Interview to DOCX', href: '/transcribe/interview-to-docx', body: 'Export interview transcripts as editable Word documents.' },
        { label: 'Video to SRT subtitles', href: '/transcribe/video-to-srt', body: 'Create timed subtitle files from video recordings.' },
        { label: 'VTT to DOCX', href: '/compare/vtt-to-docx', body: 'Convert caption text into an editable transcript workflow.' },
        { label: 'Transcribe Chrome tab audio', href: '/features/record-tab-audio', body: 'Capture Chrome or Edge tab and microphone audio, then turn it into a transcript.' },
        { label: 'Voice speed changer', href: '/tools/audio-speed-changer', body: 'Slow down or speed up speech recordings in your browser.' },
        { label: 'Transcribe from Google Drive link', href: '/transcribe/google-drive-to-text', body: 'Paste a public Google Drive file link and convert it to text.' },
      ],
      heroProofLabel: 'Transcription experience and user feedback',
      heroProof1: 'No credit card',
      heroProof2: '3 free transcripts daily',
      heroProof3: 'Private by default',
      heroFeature1: 'Use the online transcriber free (3/day)',
      heroFeature2: 'Speaker labels and timestamps included',
      heroFeature3: 'Export TXT, DOCX, PDF, SRT, and VTT',
      heroFeature4: 'Upload audio/video, import links, or record tab + mic',
      trust: 'Trusted by 10,000+ users. Free to start and upgrade only when you need more.',
      previewLabel: 'InstantTranscriber transcript preview',
      previewTitle: 'New transcript',
      previewStatus: 'Ready in minutes',
      previewUpload: 'Upload or paste a link',
      previewUploadDetail: 'YouTube, MP3, MP4, Drive',
      previewRecord: 'Record browser audio',
      previewRecordDetail: 'Tab audio plus microphone',
      previewProcessing: 'AI is adding speakers, timestamps, and summary',
      previewTranscriptTitle: 'Interview transcript',
      previewTranscriptMeta: '2 speakers',
      previewSpeaker1: 'Speaker 1',
      previewLine1: 'We can use the transcript for quotes, notes, and captions right away.',
      previewSpeaker2: 'Speaker 2',
      previewLine2: 'Great. Export the highlights and send the DOCX to the team.',
      previewStat1: '3-5 min',
      previewStat1Label: 'typical 60 min file',
      previewStat2: '90+',
      previewStat2Label: 'languages',
      previewStat3: '5',
      previewStat3Label: 'export formats',
      howTitle: 'How InstantTranscriber Works',
      howSubtitle: 'Three simple steps to get accurate transcripts fast',
      step1Title: 'Record, Upload, or Paste Link',
      step1Body: 'Record your microphone, capture a Chrome/Edge tab with microphone, upload a file, or paste a public YouTube link with captions, Google Drive, SoundCloud, podcast, or media URL',
      step2Title: 'Wait a Few Minutes',
      step2Body: 'Our AI analyzes and processes your audio (extracts audio from video if needed)',
      step3Title: 'Get Your Transcript',
      step3Body: 'Download as TXT, SRT, VTT, DOCX, or PDF',
      startFree: 'Start free',
      instantTitle: 'Get Instant Transcripts from Audio and Video',
      instantSubtitle:
        'Need text quickly? InstantTranscriber turns recordings into searchable transcripts in minutes, with speaker labels, timestamps, and exports for editing, captions, or sharing.',
      instantCard1Title: 'New: Import YouTube captions from a link',
      instantCard1Body: 'Paste a public YouTube watch, youtu.be, or Shorts link. If captions are available, they become one searchable transcript job without downloading the media.',
      instantCard1Link: 'See how YouTube caption import works.',
      instantCard1Href: '/features/youtube-caption-import',
      instantCard2Title: 'Instant AI transcription for audio and video',
      instantCard2Body: 'Convert MP3, M4A, WAV, MP4, MOV, Google Drive files, SoundCloud tracks, YouTube captions, and more into clean text you can search, quote, edit, or export.',
      instantCard3Title: 'Download transcripts your way',
      instantCard3Body: 'Export TXT for notes, DOCX or PDF for review, and SRT or VTT for captions and accessibility workflows.',
      whyTitle: 'Why Choose InstantTranscriber for Instant Transcription?',
      whySubtitle: 'Powerful features that make instant, accurate transcription simple',
      fastTitle: 'Fast & Accurate',
      fastBody: 'AI optimized for instant transcription: up to 99% accuracy on clear recordings, results in minutes.',
      easyTitle: 'Easy to Use',
      easyBody: 'Upload a file, paste a public YouTube link with captions, Google Drive, SoundCloud, podcast, or media URL, or record a Chrome/Edge browser tab with microphone. Then download the transcript in your preferred format.',
      secureTitle: 'Secure & Private',
      secureBody: 'We use bank-level encryption. Audio is auto-deleted after processing; your transcripts remain private and are not used to train AI models.',
      testimonialsTitle: 'Users save hours on transcription',
      testimonialsSubtitle: 'Real feedback from people using InstantTranscriber for interviews, research, content, and more.',
      pricingTitle: 'Simple, Transparent Pricing',
      pricingSubtitle: 'Start for free, then choose the plan that fits your needs',
      billingToggleLabel: 'Billing interval',
      billingMonthly: 'Monthly',
      billingAnnual: 'Annual',
      billingSave: 'Save ${percent}%',
      billingPerMonth: '/month',
      billingMonthlyDetail: 'Billed monthly. Cancel anytime.',
      billingAnnualDetail: 'Billed ${annualTotal}/year. Cancel anytime.',
      freeForever: 'Free Forever',
      freePlan: 'Free Plan',
      noCard: 'No credit card required',
      freeFeature1: '3 transcriptions per day',
      freeFeature2: 'Max 35 minutes per file',
      freeFeature3: 'Max 50 MB per file',
      freeFeature4: 'First transcript summary included',
      mostPopular: 'Most Popular',
      proPlan: 'Premium',
      perMonthAnnual: 'per month - or ${annualProPrice}/mo billed annually (save 40%)',
      proFeature1: 'Unlimited transcriptions',
      proFeature2: 'Max 10 hours per file',
      proFeature3: 'Max 3 GB per file',
      proFeature4: 'Higher-quality speaker labels',
      proFeature5: 'Priority support',
      proFeature6: 'Transcript summaries',
      proFeature7: 'Parallel transcription jobs',
      trialNote: 'Premium starts after payment.',
      forTeams: 'For Teams',
      enterprise: 'Enterprise',
      contactPricing: 'Contact sales for pricing',
      enterpriseFeature1: 'Everything in Pro, plus:',
      enterpriseFeature2: 'Team management',
      enterpriseFeature3: 'Dedicated account manager',
      enterpriseFeature4: 'Custom integrations',
      enterpriseFeature5: '24/7 priority support',
      contactSales: 'Contact Sales',
      guarantee: '30-day money-back guarantee. Cancel anytime.',
      faqTitle: 'Frequently Asked Questions',
      allFaqs: 'View All FAQs',
      finalTitle: 'Ready to Start InstantTranscriber?',
      finalSubtitle: 'Join thousands who trust InstantTranscriber for fast, accurate audio-to-text conversion.',
      finalNote: 'No credit card required. Get 3 free transcriptions daily.',
      testimonials: [
        {
          name: 'u/imblack4',
          role: 'Reddit comment',
          quote: "Genuinely exactly what I've been looking for. The free tier is all I need. This is a godsend.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_FREE_TIER_TESTIMONIAL_URL,
          sourceLabel: 'Read original thread',
        },
        {
          name: 'u/Sensitive_Program202',
          role: 'Reddit comment',
          quote: "I've tried a few, and found InstantTranscriber to be the best. I never need to manually edit the transcribed text.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_ACCURACY_TESTIMONIAL_URL,
          sourceLabel: 'Read original thread',
        },
        {
          kind: 'reviewLink',
          sourcePlatform: 'trustpilot',
          sourceBadge: 'Trustpilot',
          title: 'Read independent reviews',
          body: 'See independent feedback from InstantTranscriber users on Trustpilot.',
          note: 'Verified external reviews',
          sourceUrl: TRUSTPILOT_REVIEWS_URL,
          sourceLabel: 'Open Trustpilot',
        },
      ],
      faq: [
        {
          question: 'Can I transcribe without signing up?',
          answer: 'Yes. Choose an audio or video file, or record with your microphone, directly on this page. Guests get up to 3 free transcriptions a day, with a limit of 35 minutes and 50 MB per file. The daily guest allowance is shared by your network. Guest transcripts remain available for 90 days in this browser; clearing browser data removes access. Create a free account to keep your history and use other devices.',
        },
        {
          question: 'How accurate is the transcription?',
          answer: 'Our AI delivers up to 99% accuracy for clear audio recordings in English. Accuracy may vary with audio quality, accents, and background noise.',
        },
        {
          question: 'Is InstantTranscriber free?',
          answer: 'Yes. You get 3 instant transcriptions per day for free with no credit card. Upgrade any time for unlimited usage.',
        },
        {
          question: 'Can I get a transcript from a YouTube link?',
          answer: 'Yes. Paste a public YouTube link and InstantTranscriber imports an available manual or auto-generated caption track as one transcript job. Videos without captions return an error.',
        },
        {
          question: 'Can I get transcripts instantly?',
          answer: 'Yes. Upload audio or video, paste a public Google Drive, SoundCloud, podcast, or media URL, or record in your browser and get text in minutes.',
          linkText: 'See the immediate transcription guide.',
        },
        {
          question: 'What is instant AI transcription for audio and video?',
          answer: 'It means using AI to convert recordings into searchable transcripts with speaker labels, timestamps, and exports like TXT, DOCX, PDF, SRT, and VTT.',
        },
        {
          question: 'What languages do you support?',
          answer: 'We support 90+ languages, including English, German, French, Spanish, Italian, Portuguese, Dutch, Swedish, Norwegian, Danish, Finnish, Polish, Czech, Russian, Ukrainian, Greek, Turkish, Arabic, Hebrew, Persian, Hindi, Bengali, Thai, Vietnamese, Indonesian, Malay, Chinese, Japanese, Korean, and Cantonese.',
          linkText: 'See the FAQ for the full list and auto-detect details.',
        },
        {
          question: 'How long does transcription take?',
          answer: 'Most files are processed within minutes. A 60-minute recording typically takes 3-5 minutes to transcribe, depending on audio quality.',
        },
        {
          question: 'Is my data secure?',
          answer: 'Yes. We use bank-level encryption to protect your files. Audio is automatically deleted after processing, your transcriptions remain private, and your content is not used to train AI models.',
        },
      ],
    },
  },
  de: {
    nav: {
      tagline: 'Audio & Video -> Text',
      homeLabel: 'InstantTranscriber Startseite',
      features: 'Funktionen',
      tools: 'Tools',
      howItWorks: 'Funktionsweise',
      pricing: 'Preise',
      useCases: 'Anwendungsfälle',
      blog: 'Blog',
      faq: 'FAQ',
      login: 'Einloggen',
      signup: 'Jetzt registrieren',
      workspace: 'Meine Transkripte',
      account: 'Mein Konto',
      guest: 'Gastzugang',
      completeSignup: 'Konto vervollständigen',
      signedIn: 'Angemeldet',
      savedToAccount: 'Deine Transkripte werden in deinem Konto gespeichert.',
      toggleMenu: 'Menü umschalten',
      language: 'Sprache',
    },
    footer: {
      tagline: 'Wandle Audio mit unserem KI-Service in Text um.',
      product: 'Produkt',
      resources: 'Ressourcen',
      company: 'Unternehmen',
      legal: 'Rechtliches',
      transcribe: 'Audio & Video transkribieren',
      exportFormats: 'Exportformate',
      api: 'Transkriptions-API',
      features: 'Funktionen',
      recordTab: 'Chrome-Tab-Audio aufnehmen',
      youtube: 'YouTube-Untertitel importieren',
      pricing: 'Preise',
      compare: 'Formate vergleichen',
      alternatives: 'Transkriptions-Alternativen',
      immediate: 'Sofort-Transkription',
      blog: 'Blog',
      faq: 'FAQ',
      about: 'Über uns',
      contact: 'Kontakt',
      affiliate: 'Affiliate Program',
      terms: 'Nutzungsbedingungen',
      privacy: 'Datenschutz',
      rights: 'Alle Rechte vorbehalten.',
      madeFor: 'Gemacht für Content-Creator weltweit',
    },
    cta: {
      google: 'Kostenlos mit Google starten',
      googleOneClick: 'Kostenlos mit Google starten (1 Klick)',
      email: 'Oder mit E-Mail starten',
      emailSignup: 'Mit E-Mail registrieren',
      login: 'Einloggen',
      microcopy: 'Sofortzugang. Keine Kreditkarte erforderlich.',
      starting: 'Wird gestartet...',
      signingIn: 'Anmeldung läuft...',
      authNotConfigured: 'Authentifizierung ist nicht konfiguriert',
      googleError: 'Registrierung mit Google fehlgeschlagen',
      pseoNote: 'Mit einem kostenlosen Konto behältst du deinen Verlauf und kannst Transkripte auf anderen Geräten nutzen.',
    },
    metadata: {
      title: 'KI-Transkription für Audio & Video | InstantTranscriber',
      description:
        'Audio und Video ohne Anmeldung in Text umwandeln. Datei hochladen oder aufnehmen, mit Sprecherlabels und Zeitstempeln exportieren. 3 kostenlose Transkripte am Tag.',
    },
    home: {
      heroEyebrow: 'AUDIO & VIDEO ONLINE TRANSKRIBIEREN',
      heroTitlePrefix: 'Audio zu Text.',
      heroTitleRest: 'Ohne Anmeldung.',
      heroSubtitle: 'Datei hochladen, Link einfügen oder aufnehmen. Mit Sprecherlabels, Zeitstempeln und Exporten.',
      heroCompletedTranscripts: 'fertige Transkripte',
      heroProofDate: 'Stand: ${date}',
      heroReviewSource: 'Reddit-Kommentar lesen',
      heroPremiumBenefit: 'Unbegrenzte Webtranskription',
      heroPremiumAnnual: '${annualTotal} jährlich abgerechnet',
      heroPremiumMonthly: 'Oder ${price} monatlich abgerechnet',
      heroPremiumLimits: 'Bis 10 Std. / 3 GB pro Datei',
      heroPremiumPlans: 'Tarife ansehen',
      premiumGoogle: 'Premium mit Google starten',
      premiumEmail: 'Mit E-Mail fortfahren',
      premiumStart: 'Premium starten',
      intentLinksLabel: 'Beliebte Transkriptionssuchen',
      intentLinksTitle: 'Beliebt:',
      intentLinks: [
        { label: 'YouTube-Transkript-Generator', href: '/features/youtube-caption-import' },
        { label: 'Sofortige KI-Transkription', href: '/immediate-transcription' },
        { label: 'Audio online transkribieren', href: '/transcribe' },
        { label: 'Audio-Geschwindigkeit online ändern', href: '/tools/audio-speed-changer' },
      ],
      heroProofLabel: 'Transkriptionserfahrung und Nutzerfeedback',
      heroProof1: 'Keine Kreditkarte',
      heroProof2: '3 freie Transkripte pro Tag',
      heroProof3: 'Standardmäßig privat',
      heroFeature1: 'Kostenlos starten mit Sofort-Transkription (3/Tag)',
      heroFeature2: 'Sprecherlabels und Zeitstempel inklusive',
      heroFeature3: 'Export als TXT, DOCX, PDF, SRT und VTT',
      heroFeature4: 'Audio/Video hochladen, Links importieren oder Tab + Mikro aufnehmen',
      trust: '10.000+ Nutzer - Kostenlos starten - Jederzeit kündbar',
      previewLabel: 'InstantTranscriber Transkript-Vorschau',
      previewTitle: 'Neues Transkript',
      previewStatus: 'In Minuten bereit',
      previewUpload: 'Hochladen oder Link einfügen',
      previewUploadDetail: 'YouTube, MP3, MP4, Drive',
      previewRecord: 'Browser-Audio aufnehmen',
      previewRecordDetail: 'Tab-Audio plus Mikrofon',
      previewProcessing: 'KI fügt Sprecher, Zeitstempel und Zusammenfassung hinzu',
      previewTranscriptTitle: 'Interview-Transkript',
      previewTranscriptMeta: '2 Sprecher',
      previewSpeaker1: 'Sprecher 1',
      previewLine1: 'Wir können das Transkript sofort für Zitate, Notizen und Untertitel nutzen.',
      previewSpeaker2: 'Sprecher 2',
      previewLine2: 'Super. Exportiere die Highlights und sende das DOCX ans Team.',
      previewStat1: '3-5 Min.',
      previewStat1Label: 'typische 60-Min.-Datei',
      previewStat2: '90+',
      previewStat2Label: 'Sprachen',
      previewStat3: '5',
      previewStat3Label: 'Exportformate',
      howTitle: 'So funktioniert InstantTranscriber',
      howSubtitle: 'Drei einfache Schritte zu schnellen, genauen Transkripten',
      step1Title: 'Aufnehmen, hochladen oder Link einfügen',
      step1Body: 'Nimm dein Mikrofon auf, erfasse Chrome/Edge-Tab plus Mikrofon, lade eine Datei hoch oder füge einen öffentlichen YouTube-Link mit Untertiteln, Google-Drive-, SoundCloud-, Podcast- oder Medien-Link ein',
      step2Title: 'Ein paar Minuten warten',
      step2Body: 'Unsere KI analysiert und verarbeitet dein Audio und extrahiert bei Bedarf Ton aus Video',
      step3Title: 'Transkript erhalten',
      step3Body: 'Als TXT, SRT, VTT, DOCX oder PDF herunterladen',
      startFree: 'Kostenlos starten',
      instantTitle: 'Sofort-Transkripte aus Audio und Video',
      instantSubtitle:
        'Du brauchst schnell Text? InstantTranscriber macht aus Aufnahmen in Minuten durchsuchbare Transkripte - mit Sprecherlabels, Zeitstempeln und Exporten für Bearbeitung, Untertitel oder Sharing.',
      instantCard1Title: 'Neu: YouTube-Untertitel per Link importieren',
      instantCard1Body: 'Füge einen öffentlichen YouTube-, youtu.be- oder Shorts-Link ein. Wenn Untertitel verfügbar sind, werden sie ohne Mediendownload zu einem durchsuchbaren Transkript-Job.',
      instantCard1Link: 'So funktioniert der YouTube-Untertitelimport.',
      instantCard1Href: '/features/youtube-caption-import',
      instantCard2Title: 'KI-Transkription für Audio und Video',
      instantCard2Body: 'Wandle MP3, M4A, WAV, MP4, MOV, Google-Drive-Dateien, SoundCloud-Tracks, YouTube-Untertitel und mehr in sauberen Text um, den du durchsuchen, zitieren, bearbeiten oder exportieren kannst.',
      instantCard3Title: 'Transkripte passend exportieren',
      instantCard3Body: 'Exportiere TXT für Notizen, DOCX oder PDF für Reviews und SRT oder VTT für Untertitel und Barrierefreiheit.',
      whyTitle: 'Warum InstantTranscriber für Sofort-Transkription?',
      whySubtitle: 'Starke Funktionen, die schnelle und genaue Transkription einfach machen',
      fastTitle: 'Schnell & genau',
      fastBody: 'KI optimiert für Sofort-Transkription: bis zu 99% Genauigkeit bei klaren Aufnahmen, Ergebnisse in Minuten.',
      easyTitle: 'Einfach zu bedienen',
      easyBody: 'Lade eine Datei hoch, füge einen öffentlichen YouTube-Link mit Untertiteln, Google-Drive-, SoundCloud-, Podcast- oder Medien-Link ein oder nimm einen Chrome/Edge-Browser-Tab mit Mikrofon auf. Danach kannst du das Transkript im passenden Format herunterladen.',
      secureTitle: 'Sicher & privat',
      secureBody: 'Wir nutzen starke Verschlüsselung. Audio wird nach der Verarbeitung automatisch gelöscht; Transkripte bleiben privat und werden nicht zum Training von KI-Modellen verwendet.',
      testimonialsTitle: 'Nutzer sparen Stunden bei der Transkription',
      testimonialsSubtitle: 'Echtes Feedback von Menschen, die InstantTranscriber für Interviews, Forschung, Content und mehr nutzen.',
      pricingTitle: 'Einfache, transparente Preise',
      pricingSubtitle: 'Kostenlos starten und später den passenden Plan wählen',
      billingToggleLabel: 'Abrechnungsintervall',
      billingMonthly: 'Monatlich',
      billingAnnual: 'Jährlich',
      billingSave: '${percent}% sparen',
      billingPerMonth: '/Monat',
      billingMonthlyDetail: 'Monatlich abgerechnet. Jederzeit kündbar.',
      billingAnnualDetail: '${annualTotal}/Jahr abgerechnet. Jederzeit kündbar.',
      freeForever: 'Dauerhaft kostenlos',
      freePlan: 'Free Plan',
      noCard: 'Keine Kreditkarte erforderlich',
      freeFeature1: '3 Transkriptionen pro Tag',
      freeFeature2: 'Max. 35 Minuten pro Datei',
      freeFeature3: 'Max. 50 MB pro Datei',
      freeFeature4: 'Erste Transkript-Zusammenfassung inklusive',
      mostPopular: 'Am beliebtesten',
      proPlan: 'Premium',
      perMonthAnnual: 'pro Monat - oder ${annualProPrice}/Monat bei jährlicher Zahlung (40% sparen)',
      proFeature1: 'Unbegrenzte Transkriptionen',
      proFeature2: 'Max. 10 Stunden pro Datei',
      proFeature3: 'Max. 3 GB pro Datei',
      proFeature4: 'Höhere Qualität bei Sprecherlabels',
      proFeature5: 'Priorisierter Support',
      proFeature6: 'Transkript-Zusammenfassungen',
      proFeature7: 'Parallele Transkriptions-Jobs',
      trialNote: 'Premium wird nach der Zahlung aktiviert.',
      forTeams: 'Für Teams',
      enterprise: 'Enterprise',
      contactPricing: 'Preis auf Anfrage',
      enterpriseFeature1: 'Alles aus Pro, plus:',
      enterpriseFeature2: 'Teamverwaltung',
      enterpriseFeature3: 'Dedizierter Account Manager',
      enterpriseFeature4: 'Individuelle Integrationen',
      enterpriseFeature5: '24/7 Priority Support',
      contactSales: 'Sales kontaktieren',
      guarantee: '30 Tage Geld-zurück-Garantie. Jederzeit kündbar.',
      faqTitle: 'Häufige Fragen',
      allFaqs: 'Alle FAQs ansehen',
      finalTitle: 'Bereit für InstantTranscriber?',
      finalSubtitle: 'Schließe dich Tausenden an, die InstantTranscriber für schnelle und genaue Audio-zu-Text-Transkription nutzen.',
      finalNote: 'Keine Kreditkarte erforderlich. 3 kostenlose Transkriptionen pro Tag.',
      testimonials: [
        {
          name: 'u/imblack4',
          role: 'Reddit-Kommentar',
          quote: "Genuinely exactly what I've been looking for. The free tier is all I need. This is a godsend.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_FREE_TIER_TESTIMONIAL_URL,
          sourceLabel: 'Originalthread lesen',
        },
        {
          name: 'u/Sensitive_Program202',
          role: 'Reddit-Kommentar',
          quote: "I've tried a few, and found InstantTranscriber to be the best. I never need to manually edit the transcribed text.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_ACCURACY_TESTIMONIAL_URL,
          sourceLabel: 'Originalthread lesen',
        },
        {
          kind: 'reviewLink',
          sourcePlatform: 'trustpilot',
          sourceBadge: 'Trustpilot',
          title: 'Unabhängige Bewertungen lesen',
          body: 'Sieh dir unabhängiges Feedback von InstantTranscriber-Nutzern auf Trustpilot an.',
          note: 'Verifizierte externe Bewertungen',
          sourceUrl: TRUSTPILOT_REVIEWS_URL,
          sourceLabel: 'Trustpilot öffnen',
        },
      ],
      faq: [
        {
          question: 'Kann ich ohne Anmeldung transkribieren?',
          answer: 'Ja. Wähle direkt auf dieser Seite eine Audio- oder Videodatei oder nimm mit deinem Mikrofon auf. Als Gast erhältst du bis zu 3 kostenlose Transkripte pro Tag, jeweils bis zu 35 Minuten und 50 MB. Das Tageslimit gilt gemeinsam für dein Netzwerk. Gast-Transkripte bleiben 90 Tage in diesem Browser verfügbar; beim Löschen der Browserdaten geht der Zugriff verloren. Mit einem kostenlosen Konto behältst du deinen Verlauf und kannst andere Geräte nutzen.',
        },
        {
          question: 'Wie genau ist die Transkription?',
          answer: 'Unsere KI erreicht bei klaren Audioaufnahmen bis zu 99% Genauigkeit. Die Genauigkeit hängt von Audioqualität, Akzenten und Hintergrundgeräuschen ab.',
        },
        {
          question: 'Ist InstantTranscriber kostenlos?',
          answer: 'Ja. Du bekommst 3 Sofort-Transkriptionen pro Tag kostenlos und ohne Kreditkarte. Für unbegrenzte Nutzung kannst du jederzeit upgraden.',
        },
        {
          question: 'Kann ich ein Transkript aus einem YouTube-Link erhalten?',
          answer: 'Ja. Füge einen öffentlichen YouTube-Link ein und InstantTranscriber importiert verfügbare manuelle oder automatisch erzeugte Untertitel als einen Transkript-Job. Videos ohne Untertitel liefern einen Fehler.',
        },
        {
          question: 'Kann ich Transkripte sofort bekommen?',
          answer: 'Ja. Lade Audio oder Video hoch, füge eine öffentliche Google-Drive-, SoundCloud-, Podcast- oder Medien-URL ein oder nimm im Browser auf und erhalte Text in Minuten.',
          linkText: 'Sieh dir den Guide zur Sofort-Transkription an.',
        },
        {
          question: 'Was ist KI-Sofort-Transkription für Audio und Video?',
          answer: 'KI wandelt Aufnahmen in durchsuchbare Transkripte mit Sprecherlabels, Zeitstempeln und Exporten wie TXT, DOCX, PDF, SRT und VTT um.',
        },
        {
          question: 'Welche Sprachen werden unterstützt?',
          answer: 'Wir unterstützen 90+ Sprachen, darunter Englisch, Deutsch, Französisch, Spanisch, Italienisch, Portugiesisch, Niederländisch, Polnisch, Türkisch, Arabisch, Hindi, Chinesisch, Japanisch und Koreanisch.',
          linkText: 'Die komplette Liste und Auto-Erkennung findest du in der FAQ.',
        },
        {
          question: 'Wie lange dauert die Transkription?',
          answer: 'Die meisten Dateien sind in wenigen Minuten fertig. Eine 60-minutige Aufnahme dauert typischerweise 3-5 Minuten, je nach Audioqualität.',
        },
        {
          question: 'Sind meine Daten sicher?',
          answer: 'Ja. Wir schützen Dateien mit starker Verschlüsselung. Audio wird nach der Verarbeitung automatisch gelöscht, Transkripte bleiben privat und Inhalte werden nicht zum Training von KI-Modellen genutzt.',
        },
      ],
    },
  },
  fr: {
    nav: {
      tagline: 'Audio & video -> texte',
      homeLabel: 'Accueil InstantTranscriber',
      features: 'Fonctionnalites',
      tools: 'Outils',
      howItWorks: 'Fonctionnement',
      pricing: 'Tarifs',
      useCases: 'Usages',
      blog: 'Blog',
      faq: 'FAQ',
      login: 'Connexion',
      signup: 'S inscrire',
      workspace: 'Mes transcriptions',
      account: 'Mon compte',
      guest: 'Session invité',
      completeSignup: 'Finaliser l’inscription',
      signedIn: 'Connecté',
      savedToAccount: 'Vos transcriptions sont enregistrées dans votre compte.',
      toggleMenu: 'Ouvrir le menu',
      language: 'Langue',
    },
    footer: {
      tagline: 'Convertissez votre audio en texte avec notre service d IA.',
      product: 'Produit',
      resources: 'Ressources',
      company: 'Entreprise',
      legal: 'Legal',
      transcribe: 'Transcrire audio & video',
      exportFormats: 'Formats d export',
      api: 'API de transcription',
      features: 'Fonctionnalites',
      recordTab: 'Enregistrer l audio d un onglet Chrome',
      youtube: 'Importer les sous-titres YouTube',
      pricing: 'Tarifs',
      compare: 'Comparer les formats',
      alternatives: 'Alternatives de transcription',
      immediate: 'Transcription immediate',
      blog: 'Blog',
      faq: 'FAQ',
      about: 'A propos',
      contact: 'Contact',
      affiliate: 'Programme affilie',
      terms: 'Conditions d utilisation',
      privacy: 'Politique de confidentialite',
      rights: 'Tous droits reserves.',
      madeFor: 'Concu pour les createurs de contenu',
    },
    cta: {
      google: 'Commencer gratuitement avec Google',
      googleOneClick: 'Commencer avec Google (1 clic)',
      email: 'Ou commencer avec votre e-mail',
      emailSignup: 'S inscrire avec une adresse e-mail',
      login: 'Connexion',
      microcopy: 'Acces immediat. Aucune carte bancaire requise.',
      starting: 'Demarrage...',
      signingIn: 'Connexion...',
      authNotConfigured: 'Le service d authentification n est pas configure',
      googleError: 'Echec de l inscription avec Google',
      pseoNote: 'Un compte gratuit permet de conserver votre historique et de retrouver vos transcriptions sur d’autres appareils.',
    },
    metadata: {
      title: 'Transcription IA audio et vidéo | InstantTranscriber',
      description:
        'Transcrivez audio et vidéo sans inscription. Importez ou enregistrez, puis exportez le texte avec locuteurs et horodatages. 3 transcriptions gratuites par jour.',
    },
    home: {
      heroEyebrow: 'TRANSCRIPTION AUDIO ET VIDÉO EN LIGNE',
      heroTitlePrefix: 'Votre audio en texte.',
      heroTitleRest: 'Sans inscription.',
      heroSubtitle: 'Importez, collez un lien ou enregistrez. Obtenez locuteurs, horodatages et exports.',
      heroCompletedTranscripts: 'transcriptions terminées',
      heroProofDate: 'Au ${date}',
      heroReviewSource: 'Lire le commentaire Reddit',
      heroPremiumBenefit: 'Transcription web illimitée',
      heroPremiumAnnual: '${annualTotal} facturés par an',
      heroPremiumMonthly: 'Ou ${price} facturés par mois',
      heroPremiumLimits: 'Jusqu’à 10 h / 3 Go par fichier',
      heroPremiumPlans: 'Voir les offres',
      premiumGoogle: 'Activer Premium avec Google',
      premiumEmail: 'Continuer avec un e-mail',
      premiumStart: 'Activer Premium',
      intentLinksLabel: 'Recherches populaires de transcription',
      intentLinksTitle: 'Populaire :',
      intentLinks: [
        { label: 'Generateur transcription YouTube', href: '/features/youtube-caption-import' },
        { label: 'Transcription IA instantanee', href: '/immediate-transcription' },
        { label: 'Transcrire audio en ligne', href: '/transcribe' },
        { label: 'Changer la vitesse audio en ligne', href: '/tools/audio-speed-changer' },
      ],
      heroProofLabel: 'Expérience de transcription et avis utilisateurs',
      heroProof1: 'Aucune carte bancaire',
      heroProof2: '3 transcriptions gratuites par jour',
      heroProof3: 'Prive par defaut',
      heroFeature1: 'Commencez gratuitement (3 transcriptions/jour)',
      heroFeature2: 'Locuteurs et horodatages inclus',
      heroFeature3: 'Exports TXT, DOCX, PDF, SRT et VTT',
      heroFeature4: 'Import audio/video, liens, ou enregistrement onglet + micro',
      trust: '10 000+ utilisateurs - Gratuit pour commencer - Annulation a tout moment',
      previewLabel: 'Apercu de transcription InstantTranscriber',
      previewTitle: 'Nouvelle transcription',
      previewStatus: 'Prete en quelques minutes',
      previewUpload: 'Importer ou coller un lien',
      previewUploadDetail: 'YouTube, MP3, MP4, Drive',
      previewRecord: 'Enregistrer le navigateur',
      previewRecordDetail: 'Audio onglet plus micro',
      previewProcessing: 'L IA ajoute locuteurs, horodatages et resume',
      previewTranscriptTitle: 'Transcription interview',
      previewTranscriptMeta: '2 locuteurs',
      previewSpeaker1: 'Locuteur 1',
      previewLine1: 'Nous pouvons utiliser la transcription pour les citations, notes et sous-titres.',
      previewSpeaker2: 'Locuteur 2',
      previewLine2: 'Parfait. Exportez les moments forts et envoyez le DOCX a l equipe.',
      previewStat1: '3-5 min',
      previewStat1Label: 'fichier de 60 min',
      previewStat2: '90+',
      previewStat2Label: 'langues',
      previewStat3: '5',
      previewStat3Label: 'formats d export',
      howTitle: 'Comment fonctionne InstantTranscriber',
      howSubtitle: 'Trois etapes simples pour obtenir rapidement des transcriptions precises',
      step1Title: 'Enregistrez, importez ou collez un lien',
      step1Body: 'Enregistrez votre micro, capturez un onglet Chrome/Edge avec micro, importez un fichier ou collez un lien YouTube public avec sous-titres, Google Drive, SoundCloud, podcast ou media',
      step2Title: 'Attendez quelques minutes',
      step2Body: 'Notre IA analyse et traite votre audio, et extrait le son des videos si necessaire',
      step3Title: 'Recuperez votre transcription',
      step3Body: 'Telechargez en TXT, SRT, VTT, DOCX ou PDF',
      startFree: 'Commencer gratuitement',
      instantTitle: 'Obtenez des transcriptions instantanees depuis audio et video',
      instantSubtitle:
        'Besoin de texte rapidement ? InstantTranscriber transforme vos enregistrements en transcriptions consultables en quelques minutes, avec locuteurs, horodatages et exports pour l edition, les sous-titres ou le partage.',
      instantCard1Title: 'Nouveau : importer les sous-titres YouTube par lien',
      instantCard1Body: 'Collez un lien public YouTube, youtu.be ou Shorts. Si des sous-titres existent, ils deviennent un seul job consultable sans telecharger le media.',
      instantCard1Link: 'Voir comment fonctionne l import YouTube.',
      instantCard1Href: '/features/youtube-caption-import',
      instantCard2Title: 'Transcription IA pour audio et video',
      instantCard2Body: 'Convertissez MP3, M4A, WAV, MP4, MOV, fichiers Google Drive, pistes SoundCloud, sous-titres YouTube et plus encore en texte propre, consultable et exportable.',
      instantCard3Title: 'Exportez vos transcriptions comme vous voulez',
      instantCard3Body: 'Exportez en TXT pour les notes, DOCX ou PDF pour la relecture, SRT ou VTT pour les sous-titres et l accessibilite.',
      whyTitle: 'Pourquoi choisir InstantTranscriber ?',
      whySubtitle: 'Des fonctions puissantes pour une transcription rapide, simple et precise',
      fastTitle: 'Rapide & precis',
      fastBody: 'IA optimisee pour la transcription instantanee : jusqu a 99% de precision sur des enregistrements clairs, resultats en quelques minutes.',
      easyTitle: 'Simple a utiliser',
      easyBody: 'Importez un fichier, collez un lien YouTube public avec sous-titres, Google Drive, SoundCloud, podcast ou media, ou enregistrez un onglet Chrome/Edge avec le micro. Puis telechargez la transcription dans le format de votre choix.',
      secureTitle: 'Securise & prive',
      secureBody: 'Nous utilisons un chiffrement fort. L audio est supprime automatiquement apres traitement; vos transcriptions restent privees et ne servent pas a entrainer des modeles IA.',
      testimonialsTitle: 'Les utilisateurs gagnent des heures sur la transcription',
      testimonialsSubtitle: 'De vrais retours de personnes qui utilisent InstantTranscriber pour des interviews, la recherche, le contenu et plus encore.',
      pricingTitle: 'Tarifs simples et transparents',
      pricingSubtitle: 'Commencez gratuitement, puis choisissez le plan adapte',
      billingToggleLabel: 'Frequence de facturation',
      billingMonthly: 'Mensuel',
      billingAnnual: 'Annuel',
      billingSave: 'Economisez ${percent}%',
      billingPerMonth: '/mois',
      billingMonthlyDetail: 'Facturation mensuelle. Annulation a tout moment.',
      billingAnnualDetail: 'Facture ${annualTotal}/an. Annulation a tout moment.',
      freeForever: 'Gratuit pour toujours',
      freePlan: 'Offre gratuite',
      noCard: 'Aucune carte bancaire requise',
      freeFeature1: '3 transcriptions par jour',
      freeFeature2: '35 minutes max par fichier',
      freeFeature3: '50 Mo max par fichier',
      freeFeature4: 'Premier resume de transcription inclus',
      mostPopular: 'Le plus populaire',
      proPlan: 'Premium',
      perMonthAnnual: 'par mois - ou ${annualProPrice}/mois en annuel (economisez 40%)',
      proFeature1: 'Transcriptions illimitees',
      proFeature2: '10 heures max par fichier',
      proFeature3: '1 Go max par fichier',
      proFeature4: 'Libelles de locuteurs de qualite superieure',
      proFeature5: 'Support prioritaire',
      proFeature6: 'Resumes de transcription',
      proFeature7: 'Taches de transcription paralleles',
      trialNote: 'Premium est activé après le paiement.',
      forTeams: 'Pour les equipes',
      enterprise: 'Entreprise',
      contactPricing: 'Contactez-nous pour les tarifs',
      enterpriseFeature1: 'Tout Pro, plus :',
      enterpriseFeature2: 'Gestion d equipe',
      enterpriseFeature3: 'Responsable de compte dedie',
      enterpriseFeature4: 'Integrations personnalisees',
      enterpriseFeature5: 'Support prioritaire 24/7',
      contactSales: 'Contacter l equipe commerciale',
      guarantee: 'Garantie satisfait ou rembourse 30 jours. Annulation a tout moment.',
      faqTitle: 'Questions frequentes',
      allFaqs: 'Voir toutes les FAQ',
      finalTitle: 'Pret a commencer avec InstantTranscriber ?',
      finalSubtitle: 'Rejoignez les milliers d utilisateurs qui font confiance a InstantTranscriber pour convertir l audio en texte rapidement et precisement.',
      finalNote: 'Aucune carte bancaire requise. 3 transcriptions gratuites par jour.',
      testimonials: [
        {
          name: 'u/imblack4',
          role: 'Commentaire Reddit',
          quote: "Genuinely exactly what I've been looking for. The free tier is all I need. This is a godsend.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_FREE_TIER_TESTIMONIAL_URL,
          sourceLabel: 'Lire le fil original',
        },
        {
          name: 'u/Sensitive_Program202',
          role: 'Commentaire Reddit',
          quote: "I've tried a few, and found InstantTranscriber to be the best. I never need to manually edit the transcribed text.",
          sourcePlatform: 'reddit',
          sourceBadge: 'Reddit comment',
          sourceUrl: REDDIT_ACCURACY_TESTIMONIAL_URL,
          sourceLabel: 'Lire le fil original',
        },
        {
          kind: 'reviewLink',
          sourcePlatform: 'trustpilot',
          sourceBadge: 'Trustpilot',
          title: 'Lire des avis independants',
          body: 'Consultez les retours independants des utilisateurs d InstantTranscriber sur Trustpilot.',
          note: 'Avis externes verifies',
          sourceUrl: TRUSTPILOT_REVIEWS_URL,
          sourceLabel: 'Ouvrir Trustpilot',
        },
      ],
      faq: [
        {
          question: 'Puis-je transcrire sans inscription ?',
          answer: 'Oui. Importez un fichier audio ou vidéo, ou enregistrez avec votre microphone, directement sur cette page. Les invités bénéficient de 3 transcriptions gratuites par jour au maximum, avec une limite de 35 minutes et 50 Mo par fichier. Le quota quotidien est partagé par votre réseau. Les transcriptions restent disponibles 90 jours dans ce navigateur ; effacer les données du navigateur supprime cet accès. Un compte gratuit permet de conserver votre historique et de changer d’appareil.',
        },
        {
          question: 'Quelle est la precision de la transcription ?',
          answer: 'Notre IA atteint jusqu a 99% de precision sur des audios clairs. La precision depend de la qualite audio, des accents et du bruit de fond.',
        },
        {
          question: 'InstantTranscriber est-il gratuit ?',
          answer: 'Oui. Vous avez 3 transcriptions instantanees gratuites par jour, sans carte bancaire. Vous pouvez passer a Pro a tout moment pour un usage illimite.',
        },
        {
          question: 'Puis-je obtenir une transcription depuis un lien YouTube ?',
          answer: 'Oui. Collez un lien YouTube public et InstantTranscriber importe une piste de sous-titres manuelle ou automatique disponible comme un seul job. Une video sans sous-titres renvoie une erreur.',
        },
        {
          question: 'Puis-je obtenir une transcription immediatement ?',
          answer: 'Oui. Importez audio ou video, collez une URL publique Google Drive, SoundCloud, podcast ou media, ou enregistrez dans votre navigateur pour obtenir le texte en quelques minutes.',
          linkText: 'Voir le guide de transcription immediate.',
        },
        {
          question: 'Qu est-ce que la transcription IA instantanee ?',
          answer: 'C est l utilisation de l IA pour convertir des enregistrements en transcriptions consultables, avec locuteurs, horodatages et exports TXT, DOCX, PDF, SRT et VTT.',
        },
        {
          question: 'Quelles langues prenez-vous en charge ?',
          answer: 'Nous prenons en charge plus de 90 langues, dont l anglais, l allemand, le francais, l espagnol, l italien, le portugais, le neerlandais, le polonais, le turc, l arabe, l hindi, le chinois, le japonais et le coreen.',
          linkText: 'Consultez la FAQ pour la liste complete et la detection automatique.',
        },
        {
          question: 'Combien de temps dure la transcription ?',
          answer: 'La plupart des fichiers sont traites en quelques minutes. Un enregistrement de 60 minutes prend generalement 3 a 5 minutes selon la qualite audio.',
        },
        {
          question: 'Mes donnees sont-elles securisees ?',
          answer: 'Oui. Nous protegeons vos fichiers avec un chiffrement fort. L audio est supprime automatiquement apres traitement, vos transcriptions restent privees et vos contenus ne servent pas a entrainer des modeles IA.',
        },
      ],
    },
  },
};

export function getSiteCopy(locale = DEFAULT_LOCALE) {
  return siteCopy[normalizeLocale(locale)] || siteCopy[DEFAULT_LOCALE];
}
